import React, { forwardRef, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Text from '../text/Text';
import { Button } from '../button';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import TimeItemList from './TimeItemList';
import { Input, InputProps } from '../input';
import { addingZeroToNumber } from '../../utils/number';

const FORMAT = {
  TWELVE_FORMAT: '12',
  TWENTY_FOUR_FORMAT: '24',
};

export interface TimePickerProps extends InputProps {
  format?: string;
  onSelectTime: (data: string) => void;
}

// eslint-disable-next-line react/display-name
const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(
  (
    {
      name,
      placeholder,
      disabled,
      className,
      defaultValue = '',
      onSelectTime,
      format = FORMAT.TWELVE_FORMAT,
      ...props
    }: TimePickerProps,
    ref,
  ) => {
    const [time, setTime] = useState(defaultValue);
    const [open, setIsOpen] = useState(false);
    const [hour, setHour] = useState('12');
    const [minute, setMinute] = useState('00');
    const [meridiem, setMeridiem] = useState('AM');

    useEffect(() => {
      if (defaultValue) {
        setTime(defaultValue);
        if (FORMAT.TWELVE_FORMAT === format) {
          setHour(
            addingZeroToNumber(parseInt(defaultValue?.split(':')[0], 10)),
          );
          setMinute(
            addingZeroToNumber(
              parseInt(defaultValue?.split(':')[1]?.split(' ')[0], 10),
            ),
          );
          setMeridiem(
            addingZeroToNumber(defaultValue?.split(':')[1]?.split(' ')[1]),
          );
        }
        if (FORMAT.TWENTY_FOUR_FORMAT === format) {
          setHour(
            addingZeroToNumber(parseInt(defaultValue?.split(':')[0], 10)),
          );
          setMinute(
            addingZeroToNumber(parseInt(defaultValue?.split(':')[1], 10)),
          );
        }
      }
    }, [defaultValue]);

    const wrapperRef = useRef(null);
    useOnClickOutside(wrapperRef, () => setIsOpen(false));

    const onToggle = () => {
      const valueArray = time?.split(/:| /);
      setIsOpen(!open);
      setHour(valueArray?.[0] || hour);
      setMinute(valueArray?.[1] || minute);
      setMeridiem(valueArray?.[2] || meridiem);
    };

    const onSelectNow = () => {
      const date = new Date();
      const hour = date.getHours();
      const minute = date.getMinutes();
      const meridiem = hour < 12 ? 'AM' : 'PM';

      const data = `${
        hour > 12 ? addingZeroToNumber(hour - 12) : addingZeroToNumber(hour)
      }:${addingZeroToNumber(minute)} ${meridiem}`;

      setTime(data);
      onSelectTime && onSelectTime(data);
      setIsOpen(false);
    };

    const onSelect = () => {
      const data = `${hour}:${minute}${
        format === FORMAT.TWELVE_FORMAT ? ` ${meridiem}` : ''
      }`;
      setTime(data);
      onSelectTime && onSelectTime(data);
      setIsOpen(false);
    };

    return (
      <div className={classNames(className, 'position-relative w-100')}>
        <div className="mb-3" onClick={!disabled ? () => onToggle() : null}>
          <Input
            ref={ref}
            {...props}
            id={name}
            name={name}
            className="w-100 text-left text-decoration-none rounded-4"
            controlling
            value={time}
            placeholder={placeholder}
            disabled={disabled}
            readOnly
          />
        </div>

        <div
          className="time-picker bg-white position-absolute scrollbar shadow-2xl rounded-4 z-1"
          ref={wrapperRef}
        >
          {open && (
            <div className="d-flex flex-column py-6">
              <div className="d-flex justify-content-between py-3 px-4">
                <Text weight="bold">{`${hour}:${minute} ${
                  format === FORMAT.TWELVE_FORMAT ? meridiem : ''
                }`}</Text>
                <div className="pointer" onClick={() => onSelectNow()}>
                  <Text color="dark" weight="bold">
                    Now
                  </Text>
                </div>
              </div>

              <div className="d-flex justify-content-center p-5">
                <TimeItemList
                  type="Hour"
                  count={format === FORMAT.TWELVE_FORMAT ? 12 : 24}
                  active={hour}
                  onClick={(data) => setHour(data)}
                />
                <TimeItemList
                  type="Minute"
                  count={60}
                  active={minute}
                  onClick={(data) => setMinute(data)}
                />
                {format === FORMAT.TWELVE_FORMAT ? (
                  <TimeItemList
                    type="AM/PM"
                    count={0}
                    active={meridiem}
                    onClick={(data) => setMeridiem(data)}
                  />
                ) : null}
              </div>

              <div className="d-flex justify-content-end gap-6 px-4">
                <div className="mr-3">
                  <Button onClick={() => setIsOpen(false)}>Close</Button>
                </div>

                <Button type="primary" onClick={() => onSelect()}>
                  OK
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default TimePicker;
